import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Check, X, Plus, Trash2, AlertTriangle, TrendingUp, Package, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const ApproverReviewNew = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [iqcData, setIqcData] = useState(null);
  const [gradeAnalysis, setGradeAnalysis] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [bomParts, setBomParts] = useState([]);
  
  const [showPassedParams, setShowPassedParams] = useState(false);
  const [selectedParts, setSelectedParts] = useState({});
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [addingPartToParam, setAddingPartToParam] = useState(null);

  useEffect(() => {
    loadIQCData();
  }, []);

  const loadIQCData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const iqcId = window.location.pathname.split('/').pop();
      
      // Load IQC submission
      const iqcResponse = await axios.get(`${API_URL}/api/approver/iqc/${iqcId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Load BOM parts
      const bomResponse = await axios.get(`${API_URL}/api/approver/bom/parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (bomResponse.data.success) {
        setBomParts(bomResponse.data.parts);
      }
      
      if (iqcResponse.data.success) {
        setIqcData(iqcResponse.data);
        
        // Initialize selected parts from engineer's selection with costs
        const parts = {};
        iqcResponse.data.iqc_submission?.selected_parts?.forEach(part => {
          const partName = part.part_name;
          const quality = part.quality || 'Original';
          
          // Find cost from BOM
          let cost = 0;
          const bomPart = bomResponse.data.parts?.find(p => p.part_name === partName);
          if (bomPart) {
            const qualityVariant = bomPart.quality_variants?.find(q => q.quality === quality);
            cost = qualityVariant?.cost || 0;
          }
          
          parts[partName] = {
            selected: true,
            quality: quality,
            cost: cost
          };
        });
        setSelectedParts(parts);
        
        // Load grade analysis
        const deviceModel = iqcResponse.data.device?.brand + ' ' + iqcResponse.data.device?.model;
        const analysisResponse = await axios.get(
          `${API_URL}/api/approver/iqc/${iqcId}/grade-analysis?device_model=${encodeURIComponent(deviceModel)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (analysisResponse.data.success) {
          setGradeAnalysis(analysisResponse.data.grade_options);
          
          // Convert inventory to map
          const invMap = {};
          analysisResponse.data.inventory_data.forEach(inv => {
            invMap[inv.grade.toLowerCase()] = {
              stock: inv.stock_count,
              trendValue: inv.trend_percentage,
              trend: inv.trend_percentage >= 0 ? `↗️ +${inv.trend_percentage}%` : `↘️ ${inv.trend_percentage}%`,
              avgDays: inv.avg_days_to_sell,
              sales30d: inv.sales_last_30_days
            };
          });
          setInventoryData(invMap);
        }
      }
    } catch (error) {
      console.error('Error loading IQC data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedGrade) {
      alert('Please select a grade');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const iqcId = iqcData.iqc_submission.iqc_id;
      
      // Prepare selected parts
      const partsToSubmit = Object.entries(selectedParts)
        .filter(([_, data]) => data.selected)
        .map(([partName, data]) => ({
          part_name: partName,
          quality: data.quality,
          cost: data.cost || 0
        }));

      await axios.post(
        `${API_URL}/api/approver/approve`,
        {
          iqc_id: iqcId,
          selected_grade: selectedGrade,
          selected_parts: partsToSubmit,
          priority: 'Normal',
          approver_notes: `Approved for ${selectedGrade} grade. Total spare cost: ₹${calculateTotalSpareCost()}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`IQC Approved!\nGrade: ${selectedGrade}\nTotal Parts: ${partsToSubmit.length}\nTotal Cost: ₹${calculateTotalSpareCost()}\nRequisition created successfully.`);
      window.location.href = '/trc';
    } catch (error) {
      console.error('Error submitting approval:', error);
      alert('Error submitting approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 mx-auto mb-4" style={{ borderColor: ZOBOX_BLUE }}></div>
          <div className="text-sm text-gray-600">Loading IQC submission...</div>
        </div>
      </div>
    );
  }

  // Get data with fallbacks
  const iqcSubmission = iqcData?.iqc_submission || {};
  const device = iqcData?.device || {};
  const engineer = iqcData?.engineer || {};
  const responses = iqcSubmission.responses || [];
  
  // Separate failed and passed parameters
  const failedParams = responses.filter(r => r.triggered_parts?.length > 0);
  const passedParams = responses.filter(r => !r.triggered_parts || r.triggered_parts.length === 0);

  // Get unique parts from all responses
  const allAvailableParts = [...new Set(responses.flatMap(r => r.triggered_parts || []))];

  // Create aggregated parts view - group by part name with triggering parameters
  const aggregatedParts = {};
  failedParams.forEach(param => {
    const parts = param.triggered_parts || [];
    parts.forEach(partName => {
      if (!aggregatedParts[partName]) {
        aggregatedParts[partName] = {
          partName,
          triggeredBy: []
        };
      }
      aggregatedParts[partName].triggeredBy.push({
        paramNumber: param.parameter_number,
        paramName: param.parameter_name,
        selectedValue: param.selected_values[0]
      });
    });
  });
  
  // Convert to sales trend text
  const getSalesTrendText = (trendPercentage) => {
    if (trendPercentage >= 20) return 'HOT 🔥';
    if (trendPercentage >= 10) return 'Fast Selling ⚡';
    if (trendPercentage >= 0) return 'Not so Fast 📊';
    if (trendPercentage >= -10) return 'Slow Selling 🐌';
    return 'Non moving ❄️';
  };

  // Toggle part selection
  const togglePart = (partName) => {
    setSelectedParts(prev => {
      const isCurrentlySelected = prev[partName]?.selected;
      if (isCurrentlySelected) {
        // Deselect - remove from list
        const newParts = { ...prev };
        delete newParts[partName];
        return newParts;
      } else {
        // Select - add with default quality and get cost
        const bomPart = bomParts.find(p => p.part_name === partName);
        let cost = 0;
        let quality = 'Original';
        if (bomPart) {
          const qualityVariant = bomPart.quality_variants?.find(q => q.quality === 'Original');
          cost = qualityVariant?.cost || 0;
        }
        return {
          ...prev,
          [partName]: { selected: true, quality, cost }
        };
      }
    });
  };

  // Update part quality and recalculate cost
  const updatePartQuality = (partName, newQuality) => {
    const bomPart = bomParts.find(p => p.part_name === partName);
    let newCost = 0;
    if (bomPart) {
      const qualityVariant = bomPart.quality_variants?.find(q => q.quality === newQuality);
      newCost = qualityVariant?.cost || 0;
    }
    
    setSelectedParts(prev => ({
      ...prev,
      [partName]: {
        ...prev[partName],
        quality: newQuality,
        cost: newCost
      }
    }));
  };

  // Add part from BOM list
  const addPartFromList = (parameterName, partName) => {
    if (selectedParts[partName]?.selected) {
      alert('This part is already selected');
      return;
    }
    
    const bomPart = bomParts.find(p => p.part_name === partName);
    let cost = 0;
    let quality = 'Original';
    if (bomPart) {
      const qualityVariant = bomPart.quality_variants?.find(q => q.quality === 'Original');
      cost = qualityVariant?.cost || 0;
    }
    
    setSelectedParts(prev => ({
      ...prev,
      [partName]: { selected: true, quality, cost, addedToParam: parameterName }
    }));
    setAddingPartToParam(null);
  };

  // Remove part
  const removePart = (partName) => {
    setSelectedParts(prev => {
      const newParts = { ...prev };
      delete newParts[partName];
      return newParts;
    });
  };
  
  // Calculate total spare cost
  const calculateTotalSpareCost = () => {
    return Object.values(selectedParts)
      .filter(p => p.selected)
      .reduce((sum, part) => sum + (part.cost || 0), 0);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">IQC Approval Review</h1>
              <p className="text-sm text-gray-600 mt-1">{device.brand} {device.model} - {iqcSubmission.iqc_id}</p>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => window.location.href = '/trc'}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        
        {/* Device & Engineer Info */}
        <div className="bg-white border rounded-lg p-6">
          <div className="grid grid-cols-4 gap-6">
            <div>
              <div className="text-xs text-gray-500 mb-1">Device</div>
              <div className="text-sm font-bold text-gray-900">{device.brand} {device.model}</div>
              <div className="text-xs text-gray-500">{device.did}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Engineer</div>
              <div className="text-sm font-bold text-gray-900">{engineer.name}</div>
              <div className="text-xs text-gray-500">{engineer.id}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Status</div>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                {iqcSubmission.status || 'Pending Approval'}
              </span>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Summary</div>
              <div className="text-sm font-bold text-gray-900">
                {failedParams.length} Failed, {passedParams.length} Passed
              </div>
            </div>
          </div>
        </div>

        {/* IQC Report - Failed Parameters */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b bg-red-50">
            <h2 className="text-lg font-bold text-red-900">Failed Quality Checks</h2>
            <p className="text-sm text-red-700 mt-1">{failedParams.length} parameters need attention</p>
          </div>
          
          <div className="divide-y">
            {failedParams.map((param, idx) => (
              <div key={idx} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold">
                        {param.parameter_number}
                      </span>
                      <span className="font-semibold text-gray-900">{param.parameter_name}</span>
                    </div>
                    <div className="text-sm text-gray-700 ml-16">{param.selected_values[0]}</div>
                    {param.triggered_parts?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2 ml-16">
                        {param.triggered_parts.map((part, pidx) => (
                          <span key={pidx} className="px-2 py-1 bg-blue-50 text-blue-800 rounded text-xs">
                            → {part}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Passed Parameters Toggle */}
          {passedParams.length > 0 && (
            <div className="border-t">
              <button
                onClick={() => setShowPassedParams(!showPassedParams)}
                className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <span className="text-sm font-medium text-gray-700">
                  {passedParams.length} Passed Parameters
                </span>
                {showPassedParams ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {showPassedParams && (
                <div className="divide-y bg-gray-50">
                  {passedParams.map((param, idx) => (
                    <div key={idx} className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-bold text-gray-500">{param.parameter_number}</span>
                        <span className="text-sm text-gray-700">{param.parameter_name}</span>
                        <span className="text-xs text-gray-500 ml-auto">{param.selected_values[0]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Media Section - Images & Video */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b bg-purple-50">
            <h2 className="text-lg font-bold text-purple-900">Device Media</h2>
            <p className="text-sm text-purple-700 mt-1">Images and video captured by engineer</p>
          </div>
          
          <div className="p-6">
            {iqcSubmission.media && (iqcSubmission.media.images?.length > 0 || iqcSubmission.media.video) ? (
              <div className="space-y-4">
                {/* Images */}
                {iqcSubmission.media.images?.length > 0 && (
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Device Images</div>
                    <div className="grid grid-cols-4 gap-4">
                      {iqcSubmission.media.images.map((imgUrl, idx) => (
                        <div key={idx} className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
                          <img 
                            src={imgUrl} 
                            alt={`Device ${idx + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90"
                            onClick={() => window.open(imgUrl, '_blank')}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Video */}
                {iqcSubmission.media.video && (
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Device Video</div>
                    <video 
                      src={iqcSubmission.media.video} 
                      controls 
                      className="w-full max-w-2xl rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No media uploaded by engineer</p>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-gray-50 rounded">
              <div className="text-xs text-gray-600 mb-1">Engineer Notes:</div>
              <div className="text-sm text-gray-900">{iqcSubmission.general_notes || 'No notes provided'}</div>
            </div>
          </div>
        </div>

        {/* Part Selection - Aggregated View */}
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b" style={{ backgroundColor: ZOBOX_BLUE }}>
            <h2 className="text-lg font-bold text-white">Part Request - Aggregated View</h2>
            <p className="text-sm text-blue-100 mt-1">All required parts grouped with their triggering parameters</p>
          </div>

          <div className="p-6 space-y-4">
            {Object.values(aggregatedParts).map((partInfo, idx) => {
              const partName = partInfo.partName;
              const partData = selectedParts[partName] || {};
              const bomPart = bomParts.find(p => p.part_name === partName);
              const availableQualities = bomPart?.quality_variants?.map(q => q.quality) || ['Original', 'OEM', 'Copy'];
              
              return (
                <div key={idx} className="border-2 rounded-lg p-4 bg-gray-50" style={{
                  borderColor: partData.selected ? ZOBOX_BLUE : '#e5e7eb'
                }}>
                  {/* Part Header with Checkbox and Add Part Button */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-4 flex-1">
                      <input
                        type="checkbox"
                        checked={partData.selected || false}
                        onChange={() => togglePart(partName)}
                        className="w-6 h-6 mt-1"
                        style={{ accentColor: ZOBOX_BLUE }}
                      />
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-lg">{partName}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {bomPart?.part_category || 'Part Category'}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setAddingPartToParam(addingPartToParam === partName ? null : partName)}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add Part
                    </button>
                  </div>

                  {/* Add Part Dropdown */}
                  {addingPartToParam === partName && (
                    <div className="mb-3 p-3 bg-white border-2 border-blue-300 rounded-lg">
                      <div className="text-xs font-semibold text-gray-700 mb-2">Select additional part:</div>
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {bomParts.map((bomPartItem, bIdx) => (
                          <button
                            key={bIdx}
                            onClick={() => addPartFromList(partName, bomPartItem.part_name)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded text-sm"
                          >
                            {bomPartItem.part_name} <span className="text-xs text-gray-500">({bomPartItem.part_category})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Triggered By Parameters */}
                  <div className="ml-10 mb-3">
                    <div className="text-xs font-semibold text-gray-600 mb-2">Triggered by:</div>
                    <div className="space-y-1">
                      {partInfo.triggeredBy.map((trigger, tidx) => (
                        <div key={tidx} className="flex items-center gap-2 text-sm">
                          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-bold">
                            {trigger.paramNumber}
                          </span>
                          <span className="text-gray-700">{trigger.paramName}</span>
                          <span className="text-gray-500">→ {trigger.selectedValue}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quality and Cost Selection */}
                  {partData.selected && (
                    <div className="ml-10 flex items-center gap-4 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-700">Quality:</label>
                        <select
                          value={partData.quality || 'Original'}
                          onChange={(e) => updatePartQuality(partName, e.target.value)}
                          className="px-3 py-2 border rounded text-sm font-medium"
                          style={{ borderColor: ZOBOX_BLUE }}
                        >
                          {availableQualities.map(q => (
                            <option key={q} value={q}>{q}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1"></div>
                      <div className="text-lg font-bold text-green-700">
                        ₹{(partData.cost || 0).toLocaleString()}
                      </div>
                      <button
                        onClick={() => removePart(partName)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Remove part"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Show any additional parts added by approver */}
            {Object.entries(selectedParts)
              .filter(([pName, pData]) => pData.addedToParam && !aggregatedParts[pName])
              .map(([partName, partData], aidx) => {
                const bomPart = bomParts.find(p => p.part_name === partName);
                const availableQualities = bomPart?.quality_variants?.map(q => q.quality) || ['Original', 'OEM', 'Copy'];
                
                return (
                  <div key={`added-${aidx}`} className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                    <div className="flex items-center gap-4 mb-3">
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-lg">{partName}</div>
                        <div className="text-xs text-green-700 font-semibold mt-1">✨ Added by Approver</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {bomPart?.part_category || 'Part Category'}
                        </div>
                      </div>
                      <button
                        onClick={() => setAddingPartToParam(addingPartToParam === partName ? null : partName)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Part
                      </button>
                    </div>

                    {/* Add Part Dropdown */}
                    {addingPartToParam === partName && (
                      <div className="mb-3 p-3 bg-white border-2 border-blue-300 rounded-lg">
                        <div className="text-xs font-semibold text-gray-700 mb-2">Select additional part:</div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {bomParts.map((bomPartItem, bIdx) => (
                            <button
                              key={bIdx}
                              onClick={() => addPartFromList(partName, bomPartItem.part_name)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded text-sm"
                            >
                              {bomPartItem.part_name} <span className="text-xs text-gray-500">({bomPartItem.part_category})</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="ml-10 flex items-center gap-4 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-semibold text-gray-700">Quality:</label>
                        <select
                          value={partData.quality || 'Original'}
                          onChange={(e) => updatePartQuality(partName, e.target.value)}
                          className="px-3 py-2 border rounded text-sm font-medium"
                        >
                          {availableQualities.map(q => (
                            <option key={q} value={q}>{q}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1"></div>
                      <div className="text-lg font-bold text-green-700">
                        ₹{(partData.cost || 0).toLocaleString()}
                      </div>
                      <button
                        onClick={() => removePart(partName)}
                        className="text-red-600 hover:text-red-800 p-2"
                        title="Remove part"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}

            {/* Total Parts Cost */}
            <div className="border-t-4 pt-4" style={{ borderColor: ZOBOX_BLUE }}>
              <div className="flex items-center justify-between bg-green-50 rounded-lg p-4">
                <div className="text-lg font-bold text-gray-900">Total Spare Cost</div>
                <div className="text-2xl font-black text-green-700">
                  ₹{calculateTotalSpareCost().toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Grade Analysis Table - Only show if parts selected */}
        {Object.values(selectedParts).some(p => p.selected) && (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b bg-gradient-to-r from-green-50 to-blue-50">
              <h2 className="text-lg font-bold text-gray-900">Grade Analysis & Pricing</h2>
              <p className="text-sm text-gray-700 mt-1">Compare grades based on landing cost and RFS inventory</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b-2">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Grade</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Landing Cost<br/><span className="text-xs font-normal text-gray-500">(Device Purchase)</span></th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Parts Cost</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Labor Cost</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Total Cost</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">ZUPC Selling<br/><span className="text-xs font-normal text-gray-500">(RFS Price)</span></th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">RFS Inventory</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Sales Trend</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Select</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {gradeAnalysis.map((grade, idx) => {
                    const inv = inventoryData[grade.grade.toLowerCase()] || {};
                    const isRecommended = grade.recommendation === 'best' || grade.recommendation === 'recommended';
                    const totalSpareCost = calculateTotalSpareCost();
                    const landingCost = device.purchase_price || device.landed_cost || 15000;
                    
                    return (
                      <tr 
                        key={idx}
                        className={`hover:bg-gray-50 ${selectedGrade === grade.grade ? 'bg-blue-100 border-2 border-blue-500' : ''} ${isRecommended ? 'bg-green-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="font-bold text-gray-900 text-base">{grade.grade}</div>
                          {isRecommended && (
                            <div className="text-xs text-green-700 font-bold mt-1">⭐ Recommended</div>
                          )}
                          {grade.recommendation === 'caution' && (
                            <div className="text-xs text-yellow-700 font-medium mt-1">⚠️ Caution</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-bold text-gray-900">₹{landingCost.toLocaleString()}</td>
                        <td className="px-4 py-3 font-medium text-red-700">₹{totalSpareCost.toLocaleString()}</td>
                        <td className="px-4 py-3 text-gray-700">₹{grade.labor_cost?.toLocaleString()}</td>
                        <td className="px-4 py-3 font-bold text-orange-700 text-base">
                          ₹{(landingCost + totalSpareCost + (grade.labor_cost || 0) + (grade.overhead || 0)).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-blue-700 font-bold text-base">₹{grade.selling_price?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-bold text-gray-900">{inv.stock || 0} units</div>
                          <div className="text-xs text-gray-500">{inv.avgDays || 0} days avg</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-semibold">
                            {getSalesTrendText(inventoryData[grade.grade.toLowerCase()]?.trendValue || 0)}
                          </div>
                          <div className="text-xs text-gray-500">{inv.sales30d || 0} sold/30d</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="radio"
                            name="grade"
                            checked={selectedGrade === grade.grade}
                            onChange={() => setSelectedGrade(grade.grade)}
                            className="w-6 h-6 cursor-pointer"
                            style={{ accentColor: ZOBOX_BLUE }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Grade Legend */}
            <div className="px-6 py-4 bg-gray-50 border-t text-xs text-gray-600">
              <div className="flex items-center gap-6">
                <div><span className="font-semibold">Landing Cost:</span> Original device purchase price</div>
                <div><span className="font-semibold">ZUPC:</span> Zobox Used Phone Center (Ready for Sale price)</div>
                <div><span className="font-semibold">RFS:</span> Ready for Sale inventory</div>
              </div>
            </div>
          </div>
        )}

        {/* Submit Button */}
        {selectedGrade && (
          <div className="sticky bottom-0 bg-white border-t-4 shadow-lg rounded-lg p-6" style={{ borderTopColor: ZOBOX_BLUE }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Selected Grade:</div>
                <div className="text-2xl font-bold text-gray-900">{selectedGrade}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {Object.values(selectedParts).filter(p => p.selected).length} parts selected
                </div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-8 py-4 text-white rounded-lg text-lg font-bold disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#10b981' }}
              >
                {submitting ? 'Submitting...' : 'Approve & Create Requisition'}
                <Check className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproverReviewNew;
